import { Component, OnDestroy, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from '@app/core/services/notification.service';
import { OpenDialogService } from '@app/core/services/open-dialog.service';
import { DealerStateSelectors } from '@app/core/states/dealer/dealer-state.selectors';
import {
  RegisterMobilbytteFormModel,
  RegisterMobilbytteStepperComponent,
} from './register-mobilbytte-stepper/register-mobilbytte-stepper.component';
import { CustomerProductMobilePhoneSelectors } from '@app/sales/states/customer-product-mobilephone/customer-product-mobilephone.selectors';
import { CustomerServiceProductSelectors } from '@app/sales/states/customer-service-product/customer-service-product.selectors';
import { IconNames } from '@app/shared/enums/icon-names';
import { select, Store } from '@ngxs/store';
import { IceIconButtonComponent } from '../../../../../shared/components/atoms/ice-icon-button/ice-icon-button.component';
import { PhoneCardComponent } from '../../../../../shared/components/phone-card/phone-card.component';
import { CustomerEquipmentUpgradeAgreement } from '../../models/equipment-upgrade-agreement.model';
import { MobilBytteSelectors } from '../../states/mobilbytte/mobilbytte.selectors';
import { ControlInquiryComponent } from '../control-inquiry/control-inquiry.component';
import { InformationDisclosureComponent } from '../information-disclosure/information-disclosure.component';
import { MobilePhoneHeaderComponent } from '../mobilephone-mobilbytte-bytte-search/mobilephone-mobilbytte-bytte-details/mobilephone-header/mobilephone-header.component';
import { MobilBytteHelperService } from '@app/sales/services/mobilbytte-helper.service';
import { FormGroup } from '@angular/forms';
import { RegisterMobilBytteBytteAction } from '../../states/mobilbytte/mobilbytte.actions';
import { take } from 'rxjs';
import { OSAffectedControlType } from '../../models/trade-in-enums';
import { RemoveMobileProductAction } from '@app/sales/states/customer-product-mobilephone/customer-product-mobilephone.actions';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomerProductSelectors } from '@app/sales/states/customer-product/customer-product.selectors';
import { IceAlertComponent } from '@app/shared/components/molecules/ice-alert/ice-alert.component';

@Component({
  selector: 'app-register-mobilbytte-dialog',
  imports: [
    IceIconButtonComponent,
    MatDialogClose,
    MobilePhoneHeaderComponent,
    RegisterMobilbytteStepperComponent,
    PhoneCardComponent,
    ControlInquiryComponent,
    InformationDisclosureComponent,
    IceAlertComponent,
  ],
  templateUrl: './register-mobilbytte-dialog.component.html',
  styleUrl: './register-mobilbytte-dialog.component.scss',
})
export class RegisterMobilbytteDialogComponent implements OnDestroy {
  private openDialogService = inject(OpenDialogService);
  private store = inject(Store);
  private router = inject(Router);
  private dialogRef = inject<MatDialogRef<RegisterMobilbytteDialogComponent>>(MatDialogRef);
  private notificationService = inject(NotificationService);
  private mobilBytteHelperService = inject(MobilBytteHelperService);
  data = inject<{
    equipmentUpgradeAgreementId: number;
    route: ActivatedRoute;
  }>(MAT_DIALOG_DATA);

  IconNames = IconNames;

  existingMobilBytteAgreements = select(MobilBytteSelectors.existingEquipmentUpgradeAgreements);
  hasMobilBytteBytteInCart = select(MobilBytteSelectors.hasMobilBytteBytteInCart);
  assignedMobileInsurances = select(CustomerServiceProductSelectors.getAssignedMobileInsurances);
  isManualRegistration = select(DealerStateSelectors.canUseManualRegistration);
  mobilBytteProductInCart = select(CustomerProductMobilePhoneSelectors.getMobilBytteProductInCart);
  mobilePhoneProducts = select(CustomerProductMobilePhoneSelectors.mobilePhoneProducts);
  allVoiceProducts = select(CustomerProductSelectors.getAllVoiceProducts);

  mobilBytteAgreementId: number;
  inquiredControl: OSAffectedControlType | string;
  inquiredInfoDisclosure: string;
  currentStepIndex = 0;

  constructor() {
    const data = this.data;

    if (data === undefined) {
      this.notificationService.error('Noe gikk galt, vennligst prøv igjen.');
      this.dialogRef.close();
    }

    this.mobilBytteAgreementId = data?.equipmentUpgradeAgreementId || null;

    window.addEventListener('beforeunload', () => {
      this.ngOnDestroy();
    });
  }

  get mobilBytteAgreement(): CustomerEquipmentUpgradeAgreement {
    if (!this.mobilBytteAgreementId) {
      return null;
    }

    const agreement =
      this.existingMobilBytteAgreements()?.find(agreement => +agreement.agreementId === this.mobilBytteAgreementId) || null;
    return agreement;
  }

  get insuranceName(): string {
    const insuranceProductNumber = this.mobilBytteAgreement?.insuranceServiceId;
    const assignedInsurance = this.assignedMobileInsurances()?.find(
      insurance => insurance.assignedInsurance?.productNo === +insuranceProductNumber
    )?.assignedInsurance;

    return assignedInsurance?.monthlyCost?.description || 'Ingen forsikring';
  }

  get insurancePrice(): number {
    return this.mobilBytteAgreement?.monthlyInsuranceAmount?.includingVat ?? 0;
  }

  goBack(): void {
    this.openDialogService.openMobilephoneMobilbytteBytteSearchDialog(
      this.mobilBytteAgreementId,
      this.isManualRegistration(),
      this.data.route
    );
  }

  submit(formData: FormGroup<RegisterMobilbytteFormModel>): void {
    const request = this.mobilBytteHelperService.createRegisterMobilbytteRequest(formData, this.mobilBytteAgreementId);

    this.store
      .dispatch(new RegisterMobilBytteBytteAction(request, this.mobilBytteAgreementId))
      .pipe(take(1))
      .subscribe(() => {
        this.dialogRef.close();

        const mobileProductConnectedToMobilbytte = this.mobilePhoneProducts().find(
          p => +p.existingAgreementId === this.mobilBytteAgreementId
        );
        const voiceProductConnectedToMobilbytte = this.allVoiceProducts().find(
          voiceProduct => voiceProduct.id === mobileProductConnectedToMobilbytte?.voiceProductId
        );
        if (!voiceProductConnectedToMobilbytte?.isExistingSubscription) {
          this.router.navigate(['../subscription'], { relativeTo: this.data.route });
        }
      });
  }

  ngOnDestroy(): void {
    const mobileProductToRemove = this.mobilePhoneProducts().find(
      p => +p.existingAgreementId === this.mobilBytteAgreementId
    );
    if (mobileProductToRemove && !this.hasMobilBytteBytteInCart()) {
      this.store.dispatch(new RemoveMobileProductAction(mobileProductToRemove.id));
    }
  }
}
